<template>
  <div class="tender-timeline-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="timeline" color="info" class="q-mr-sm" />
            Tender Timeline
          </div>
          <div class="panel-subtitle">
            Complete history of tender activities and milestones
          </div>
        </div>
        <div class="col-auto">
          <q-btn-group>
            <q-btn
              flat
              icon="filter_list"
              :color="hasActiveFilters ? 'primary' : 'grey-7'"
              @click="showFilters = !showFilters"
            >
              <q-tooltip>Toggle Filters</q-tooltip>
            </q-btn>
            <q-btn
              flat
              icon="refresh"
              color="grey-7"
              @click="refreshTimeline"
            >
              <q-tooltip>Refresh Timeline</q-tooltip>
            </q-btn>
          </q-btn-group>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div v-if="showFilters" class="timeline-filters q-mb-lg">
      <q-card flat bordered>
        <q-card-section>
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-4">
              <q-select
                v-model="filters.type"
                :options="typeOptions"
                label="Activity Type"
                multiple
                use-chips
                outlined
                clearable
              />
            </div>
            
            <div class="col-12 col-md-4">
              <q-input
                v-model="filters.dateFrom"
                label="From Date"
                type="date"
                outlined
                clearable
              />
            </div>
            
            <div class="col-12 col-md-4">
              <q-input
                v-model="filters.dateTo"
                label="To Date"
                type="date"
                outlined
                clearable
              />
            </div>
          </div>
          
          <div class="row q-mt-md">
            <div class="col">
              <q-btn
                flat
                label="Clear Filters"
                color="grey-7"
                :disable="!hasActiveFilters"
                @click="clearFilters"
              />
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>

    <!-- Timeline -->
    <div class="timeline-container">
      <q-timeline color="primary" side="right">
        <!-- No Activities Message -->
        <q-timeline-entry
          v-if="filteredActivities.length === 0"
          icon="info"
          color="grey-5"
        >
          <template #title>
            <div class="timeline-title text-grey-6">
              {{ hasActiveFilters ? 'No activities match your filters' : 'No activities recorded yet' }}
            </div>
          </template>
          
          <div class="empty-timeline">
            <p v-if="hasActiveFilters">Try adjusting your filter criteria to see more results.</p>
            <p v-else>Activities will appear here as actions are performed on this tender.</p>
          </div>
        </q-timeline-entry>

        <!-- Activity Entries -->
        <q-timeline-entry
          v-for="activity in filteredActivities"
          :key="activity.id"
          :icon="getActivityIcon(activity.type)"
          :color="getActivityColor(activity.type)"
          :subtitle="formatActivityDate(activity.created_at)"
        >
          <template #title>
            <div class="timeline-title">
              {{ activity.title }}
              <q-badge
                :color="getActivityColor(activity.type)"
                :label="getActivityTypeLabel(activity.type)"
                class="q-ml-sm"
              />
            </div>
          </template>
          
          <div class="activity-content">
            <div class="activity-description">
              {{ activity.description }}
            </div>
            
            <!-- Activity Metadata -->
            <div class="activity-meta">
              <div class="meta-item">
                <q-icon name="person" size="16px" />
                <span>{{ activity.user_name }}</span>
              </div>
              
              <div v-if="activity.related_entity" class="meta-item">
                <q-icon name="link" size="16px" />
                <span>{{ activity.related_entity }}</span>
              </div>
            </div>
            
            <!-- Activity Details -->
            <div v-if="activity.details" class="activity-details">
              <q-expansion-item
                icon="info"
                label="View Details"
                dense
                header-class="text-primary"
              >
                <div class="details-content">
                  <div
                    v-for="(value, key) in activity.details"
                    :key="key"
                    class="detail-item"
                  >
                    <strong>{{ formatDetailKey(key) }}:</strong> {{ value }}
                  </div>
                </div>
              </q-expansion-item>
            </div>
            
            <!-- Activity Actions -->
            <div v-if="activity.actionable" class="activity-actions">
              <q-btn
                v-if="activity.type === 'quote_request'"
                flat
                size="sm"
                icon="visibility"
                label="View Quote"
                @click="viewQuote(activity.related_id)"
              />
              
              <q-btn
                v-if="activity.type === 'contractor_added'"
                flat
                size="sm"
                icon="business"
                label="View Contractor"
                @click="viewContractor(activity.related_id)"
              />
              
              <q-btn
                v-if="activity.type === 'document_uploaded'"
                flat
                size="sm"
                icon="folder"
                label="View Document"
                @click="viewDocument(activity.related_id)"
              />
            </div>
          </div>
        </q-timeline-entry>
      </q-timeline>
    </div>

    <!-- Load More Button -->
    <div v-if="hasMoreActivities" class="load-more-container">
      <q-btn
        flat
        label="Load More Activities"
        :loading="loadingMore"
        color="primary"
        @click="loadMoreActivities"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    activities: {
      type: Array,
      default: () => [],
    },
    comments: {
      type: Array,
      default: () => [],
    },
  });

  const $q = useQuasar();

  // State
  const showFilters = ref(false);
  const loadingMore = ref(false);
  const hasMoreActivities = ref(false);

  // Filters
  const filters = ref({
    type: [],
    dateFrom: '',
    dateTo: '',
  });

  // Mock activities data - in real implementation this would come from props
  const mockActivities = ref([
    {
      id: 1,
      type: 'tender_created',
      title: 'Tender Created',
      description: 'New tender "HVAC System Upgrade" was created',
      user_name: 'John Smith',
      created_at: '2024-01-15T09:00:00Z',
      details: {
        estimated_value: '$150,000',
        due_date: '2024-02-15',
        priority: 'High',
      },
    },
    {
      id: 2,
      type: 'contractor_added',
      title: 'Contractor Added',
      description: 'ABC HVAC Services was added to the tender',
      user_name: 'John Smith',
      created_at: '2024-01-15T10:30:00Z',
      related_entity: 'ABC HVAC Services',
      related_id: 1,
      actionable: true,
    },
    {
      id: 3,
      type: 'contractor_added',
      title: 'Contractor Added',
      description: 'XYZ Mechanical was added to the tender',
      user_name: 'John Smith',
      created_at: '2024-01-15T10:35:00Z',
      related_entity: 'XYZ Mechanical',
      related_id: 2,
      actionable: true,
    },
    {
      id: 4,
      type: 'document_uploaded',
      title: 'Document Uploaded',
      description: 'Technical specifications document was uploaded',
      user_name: 'Jane Doe',
      created_at: '2024-01-16T14:20:00Z',
      related_entity: 'Tender Specifications.pdf',
      related_id: 1,
      actionable: true,
      details: {
        file_size: '2.1 MB',
        file_type: 'PDF',
        category: 'Technical',
      },
    },
    {
      id: 5,
      type: 'email_sent',
      title: 'Email Sent',
      description: 'Tender invitation sent to contractors',
      user_name: 'System',
      created_at: '2024-01-16T15:00:00Z',
      details: {
        recipients: 'ABC HVAC, XYZ Mechanical',
        subject: 'Tender Invitation - HVAC System Upgrade',
      },
    },
    {
      id: 6,
      type: 'quote_request',
      title: 'Quote Created',
      description: 'Initial quote was created for the tender',
      user_name: 'John Smith',
      created_at: '2024-01-17T11:15:00Z',
      related_entity: 'Quote #001',
      related_id: 1,
      actionable: true,
      details: {
        quote_value: '$148,500',
        status: 'Draft',
      },
    },
    {
      id: 7,
      type: 'status_change',
      title: 'Status Updated',
      description: 'Tender status changed from "Pending" to "Submitted"',
      user_name: 'John Smith',
      created_at: '2024-01-18T09:30:00Z',
      details: {
        from_status: 'Pending',
        to_status: 'Submitted',
      },
    },
  ]);

  // Options
  const typeOptions = [
    { label: 'Tender Created', value: 'tender_created' },
    { label: 'Contractor Added', value: 'contractor_added' },
    { label: 'Document Uploaded', value: 'document_uploaded' },
    { label: 'Email Sent', value: 'email_sent' },
    { label: 'Quote Created', value: 'quote_request' },
    { label: 'Status Change', value: 'status_change' },
    { label: 'Comment Added', value: 'comment_added' },
  ];

  // Computed properties
  const hasActiveFilters = computed(() => {
    return filters.value.type.length > 0 || 
      filters.value.dateFrom || 
      filters.value.dateTo;
  });

  const filteredActivities = computed(() => {
    let activities = props.activities.length > 0 ? props.activities : mockActivities.value;
  
    // Filter by type
    if (filters.value.type.length > 0) {
      activities = activities.filter((activity) => 
        filters.value.type.includes(activity.type),
      );
    }
  
    // Filter by date range
    if (filters.value.dateFrom) {
      const fromDate = new Date(filters.value.dateFrom);
      activities = activities.filter((activity) => 
        new Date(activity.created_at) >= fromDate,
      );
    }
  
    if (filters.value.dateTo) {
      const toDate = new Date(filters.value.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      activities = activities.filter((activity) => 
        new Date(activity.created_at) <= toDate,
      );
    }
  
    // Sort by date (newest first)
    return activities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  });

  // Methods
  function getActivityIcon(type) {
    const icons = {
      tender_created: 'add_circle',
      contractor_added: 'business',
      document_uploaded: 'cloud_upload',
      email_sent: 'email',
      quote_request: 'description',
      status_change: 'update',
      comment_added: 'comment',
    };
    return icons[type] || 'info';
  }

  function getActivityColor(type) {
    const colors = {
      tender_created: 'positive',
      contractor_added: 'primary',
      document_uploaded: 'orange',
      email_sent: 'accent',
      quote_request: 'secondary',
      status_change: 'info',
      comment_added: 'purple',
    };
    return colors[type] || 'grey';
  }

  function getActivityTypeLabel(type) {
    const labels = {
      tender_created: 'Created',
      contractor_added: 'Contractor',
      document_uploaded: 'Document',
      email_sent: 'Email',
      quote_request: 'Quote',
      status_change: 'Status',
      comment_added: 'Comment',
    };
    return labels[type] || type;
  }

  function formatActivityDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
  
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-AU', { 
        hour: '2-digit', 
        minute: '2-digit', 
      }) + ' - Today';
    } else if (diffDays === 1) {
      return date.toLocaleTimeString('en-AU', { 
        hour: '2-digit', 
        minute: '2-digit', 
      }) + ' - Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-AU', { 
        weekday: 'long',
        hour: '2-digit', 
        minute: '2-digit', 
      });
    } else {
      return date.toLocaleDateString('en-AU', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit', 
      });
    }
  }

  function formatDetailKey(key) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  function clearFilters() {
    filters.value = {
      type: [],
      dateFrom: '',
      dateTo: '',
    };
  }

  function refreshTimeline() {
    $q.notify({
      color: 'info',
      message: 'Timeline refreshed',
      timeout: 2000,
    });
  }

  async function loadMoreActivities() {
    loadingMore.value = true;
    try {
      // Simulate loading more activities
      await new Promise((resolve) => setTimeout(resolve, 1000));
    
      $q.notify({
        color: 'info',
        message: 'No more activities to load',
        timeout: 2000,
      });
    
      hasMoreActivities.value = false;
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: 'Failed to load more activities',
        timeout: 3000,
      });
    } finally {
      loadingMore.value = false;
    }
  }

  function viewQuote(quoteId) {
    $q.notify({
      color: 'info',
      message: `Viewing quote ${quoteId}`,
      timeout: 2000,
    });
  }

  function viewContractor(contractorId) {
    $q.notify({
      color: 'info',
      message: `Viewing contractor ${contractorId}`,
      timeout: 2000,
    });
  }

  function viewDocument(documentId) {
    $q.notify({
      color: 'info',
      message: `Viewing document ${documentId}`,
      timeout: 2000,
    });
  }

  onMounted(() => {
    // Check if there are more activities to load
    hasMoreActivities.value = mockActivities.value.length >= 20;
  });
</script>

<style lang="scss" scoped>
.tender-timeline-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
  }
  
  .timeline-filters {
    background: #f8f9fa;
    border-radius: 8px;
  }
  
  .timeline-container {
    .timeline-title {
      font-weight: 600;
      color: #333;
      display: flex;
      align-items: center;
    }
    
    .activity-content {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      margin-top: 8px;
      
      .activity-description {
        color: #333;
        line-height: 1.5;
        margin-bottom: 12px;
      }
      
      .activity-meta {
        display: flex;
        gap: 16px;
        margin-bottom: 12px;
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.9rem;
          color: #666;
        }
      }
      
      .activity-details {
        margin-bottom: 12px;
        
        .details-content {
          background: white;
          border-radius: 4px;
          padding: 12px;
          margin-top: 8px;
          
          .detail-item {
            margin-bottom: 8px;
            font-size: 0.9rem;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
      
      .activity-actions {
        display: flex;
        gap: 8px;
      }
    }
    
    .empty-timeline {
      color: #666;
      font-style: italic;
      text-align: center;
      padding: 16px;
    }
  }
  
  .load-more-container {
    text-align: center;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e0e0e0;
  }
}
</style>